import React, { useState, useMemo } from 'react';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  InputLabel,
  Link,
  MenuItem,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import { makeStyles } from '@material-ui/core/styles';
import { TOAST_MESSAGE_SEVERITY_ERROR, TOAST_MESSAGE_SEVERITY_SUCCESS } from 'modules/layout/types';
import { formatDateForInput } from 'utilities/common';
import TaskCallModal, { TASK_MODAL_ACTION } from 'modules/dashboard/components/modals/TaskCallModal.component';
import TaskDueDate from 'modules/dashboard/components/TaskDueDate.component';
import TaskEmailModal from 'modules/dashboard/components/modals/TaskEmailModal.component';
import { TASK_STATUS_FILTER_OPTIONS, USER_TASK_STATUS_OPTIONS, matchesStatusFilter } from 'modules/dashboard/taskStatus';
import { isIdEqual } from 'modules/enumerations/enumerationHelpers';
import { TASK_STATUS } from 'modules/enumerations/enumerationIds';

const useStyles = makeStyles(theme => ({
  card: {
    width: '100%'
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(1)
  },
  filtersRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1),
    alignItems: 'center'
  },
  filterControl: {
    minWidth: 130
  },
  filterControlSmall: {
    minWidth: 110
  },
  tableContainer: {
    maxHeight: 420,
    overflowY: 'auto'
  },
  headerCell: {
    fontWeight: 'bold',
    backgroundColor: theme.palette.grey[100]
  },
  clickableTitle: {
    cursor: 'pointer',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  overdueRow: {
    backgroundColor: theme.palette.error.light + '20'
  },
  noTasks: {
    padding: theme.spacing(3),
    textAlign: 'center'
  },
  statusActive: { color: theme.palette.primary.main },
  statusOverdue: { color: theme.palette.error.main, fontWeight: 500 },
  statusCompleted: { color: theme.palette.success.main },
  statusCanceled: { color: theme.palette.text.secondary },
  highPriority: { color: theme.palette.error.main },
  normalPriority: { color: theme.palette.text.primary },
  lowPriority: { color: theme.palette.text.secondary },
  formControl: { width: '100%' },
  dialogContent: { minWidth: 520 }
}));

// Status filter value meaning every status; the Status filter is never empty and defaults to Active
const ALL_STATUSES = 'all';

const STATUS_CONFIG = [
  { id: '5640D88A-9546-45E2-B9F2-4AA81B178EE6', className: 'statusActive', label: 'Active' },
  { id: '3AED3B2C-6918-41E4-BDDF-65B2B08F18D2', className: 'statusOverdue', label: 'Overdue' },
  { id: '59DAD24A-41A2-4F69-849D-B3E099597DD2', className: 'statusCanceled', label: 'Canceled' },
  { id: '28125DAC-5D57-4BB4-A92E-E411415FD8C3', className: 'statusCompleted', label: 'Completed' },
  ...USER_TASK_STATUS_OPTIONS.map(s => ({ id: s.id, className: 'statusActive', label: s.title }))
];

const IMPORTANCE_CONFIG = [
  { id: '3E340180-E70F-4EC7-9C71-F4E4600F8184', className: 'lowPriority', label: 'Low' },
  { id: '7E3FAF93-13E3-48FA-815C-EDC4A715AB0F', className: 'normalPriority', label: 'Normal' },
  { id: 'A9AA0FC7-4D4C-41CD-A55B-0D88BE85EAD8', className: 'highPriority', label: 'High' }
];

// ─── Create Task Modal ─────────────────────────────────────────────────────────

const CreateFileTaskModal = ({ open, onClose, onSubmit, authorizationDetails, vendors, enumerations, isLoading }) => {
  const classes = useStyles();
  const currentUserId = useSelector(state => state.auth.profile?.internalUserId);

  const taskTypes =
    enumerations?.allEnums?.taskTypes && enumerations.allEnums.taskTypes.length > 0
      ? enumerations.allEnums.taskTypes.filter(s => s.isActive === true)
      : [];

  const taskImportances =
    enumerations?.allEnums?.taskImportances && enumerations.allEnums.taskImportances.length > 0
      ? enumerations.allEnums.taskImportances.filter(s => s.isActive === true)
      : [];

  const services =
    enumerations?.allEnums?.authCommentService && enumerations.allEnums.authCommentService.length > 0
      ? enumerations.allEnums.authCommentService.filter(s => s.isActive === true)
      : [];

  const regardings =
    enumerations?.allEnums?.authCommentRegarding && enumerations.allEnums.authCommentRegarding.length > 0
      ? enumerations.allEnums.authCommentRegarding.filter(s => s.isActive === true)
      : [];

  const [formData, setFormData] = useState({
    taskTypeId: null,
    description: '',
    dueDate: '',
    taskImportanceId: null,
    serviceId: null,
    regardingId: null,
    vendorId: null,
    taskStatusId: '5640D88A-9546-45E2-B9F2-4AA81B178EE6'
  });

  const [errors, setErrors] = useState({});

  const handleFieldChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const isVendorTask = () => {
    const taskType = taskTypes.find(t => t.id === formData.taskTypeId);
    return taskType && ['VendorCall', 'VendorEmail', 'VendorLetter', 'VendorVoucher'].includes(taskType.title);
  };

  const validate = () => {
    const newErrors = {};
    if (!formData.taskTypeId) newErrors.taskTypeId = 'Task type is required';
    if (!formData.description || !formData.description.trim()) newErrors.description = 'Task description is required';
    if (!formData.dueDate) newErrors.dueDate = 'Due date is required';
    if (!formData.taskImportanceId) newErrors.taskImportanceId = 'Importance is required';
    if (!formData.serviceId) newErrors.serviceId = 'Service is required';
    if (!formData.regardingId) newErrors.regardingId = 'Regarding is required';
    if (isVendorTask() && !formData.vendorId) newErrors.vendorId = 'Vendor is required for vendor tasks';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (validate()) {
      onSubmit({
        ...formData,
        fileId: authorizationDetails?.fileId,
        title: formData.description.substring(0, 100),
        assignedToUserId: currentUserId,
        isManuallyCreated: true,
        addedBy: currentUserId,
        modifiedBy: currentUserId
      });
    }
  };

  const handleClose = () => {
    setFormData({
      taskTypeId: null,
      description: '',
      dueDate: '',
      taskImportanceId: null,
      serviceId: null,
      regardingId: null,
      vendorId: null
    });
    setErrors({});
    onClose();
  };

  const fileLabel = authorizationDetails
    ? `${authorizationDetails.fileNumber || authorizationDetails.fileId || ''} — ${authorizationDetails.eeFullName || ''}`
    : '';

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Create Task</DialogTitle>
      <DialogContent dividers className={classes.dialogContent}>
        <Box display="flex" flexDirection="column" style={{ gap: 16 }}>
          <TextField label="File / Employee" value={fileLabel} disabled size="small" fullWidth />

          <FormControl className={classes.formControl} error={!!errors.taskTypeId} size="small">
            <InputLabel>Task Type *</InputLabel>
            <Select value={formData.taskTypeId || ''} onChange={e => handleFieldChange('taskTypeId', e.target.value)}>
              {taskTypes.map(type => (
                <MenuItem key={type.id} value={type.id}>
                  {type.title}
                </MenuItem>
              ))}
            </Select>
            {errors.taskTypeId && (
              <Typography variant="caption" color="error">
                {errors.taskTypeId}
              </Typography>
            )}
          </FormControl>

          <FormControl className={classes.formControl} error={!!errors.taskImportanceId} size="small">
            <InputLabel>Importance *</InputLabel>
            <Select value={formData.taskImportanceId || ''} onChange={e => handleFieldChange('taskImportanceId', e.target.value)}>
              {taskImportances.map(level => (
                <MenuItem key={level.id} value={level.id}>
                  {level.title}
                </MenuItem>
              ))}
            </Select>
            {errors.taskImportanceId && (
              <Typography variant="caption" color="error">
                {errors.taskImportanceId}
              </Typography>
            )}
          </FormControl>

          <TextField
            label="Task Description *"
            multiline
            rows={3}
            fullWidth
            size="small"
            value={formData.description}
            onChange={e => handleFieldChange('description', e.target.value)}
            error={!!errors.description}
            helperText={errors.description || 'What the RC should do'}
          />

          <TextField
            label="Due Date *"
            type="date"
            fullWidth
            size="small"
            value={formData.dueDate}
            onChange={e => handleFieldChange('dueDate', e.target.value)}
            error={!!errors.dueDate}
            helperText={errors.dueDate}
            InputLabelProps={{ shrink: true }}
          />

          <FormControl className={classes.formControl} error={!!errors.serviceId} size="small">
            <InputLabel>Service *</InputLabel>
            <Select value={formData.serviceId || ''} onChange={e => handleFieldChange('serviceId', e.target.value)}>
              {services.map(service => (
                <MenuItem key={service.id} value={service.id}>
                  {service.title}
                </MenuItem>
              ))}
            </Select>
            {errors.serviceId && (
              <Typography variant="caption" color="error">
                {errors.serviceId}
              </Typography>
            )}
          </FormControl>

          <FormControl className={classes.formControl} error={!!errors.regardingId} size="small">
            <InputLabel>Regarding *</InputLabel>
            <Select value={formData.regardingId || ''} onChange={e => handleFieldChange('regardingId', e.target.value)}>
              {regardings.map(regarding => (
                <MenuItem key={regarding.id} value={regarding.id}>
                  {regarding.title}
                </MenuItem>
              ))}
            </Select>
            {errors.regardingId && (
              <Typography variant="caption" color="error">
                {errors.regardingId}
              </Typography>
            )}
          </FormControl>

          {isVendorTask() && (
            <Autocomplete
              options={vendors || []}
              getOptionLabel={option => option.companyName || option.vendorName || ''}
              value={(vendors || []).find(v => v.vendorId === formData.vendorId) || null}
              onChange={(event, newValue) => handleFieldChange('vendorId', newValue?.vendorId || null)}
              renderInput={params => (
                <TextField {...params} label="Vendor *" error={!!errors.vendorId} helperText={errors.vendorId} size="small" />
              )}
            />
          )}
        </Box>
        <Box mt={1}>
          <Typography variant="caption" color="textSecondary">
            * Required field
          </Typography>
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained" disabled={isLoading}>
          Create Task
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// ─── Main Card ────────────────────────────────────────────────────────────────

const AuthorizationTasksCard = props => {
  const { tasks, vendors, enumerations, onCreateTask, authorizationDetails, isReadOnly = false } = props;
  const classes = useStyles();
  const history = useHistory();

  const taskTypes =
    enumerations?.allEnums?.taskTypes && enumerations.allEnums.taskTypes.length > 0
      ? enumerations.allEnums.taskTypes.filter(s => s.isActive === true)
      : [];

  const taskImportances =
    enumerations?.allEnums?.taskImportances && enumerations.allEnums.taskImportances.length > 0
      ? enumerations.allEnums.taskImportances.filter(s => s.isActive === true)
      : [];

  // Task.ServiceId references the AuthCommentService enumeration; inactive services still resolve for display
  const allServices = enumerations?.allEnums?.authCommentService || [];
  const services = allServices.filter(s => s.isActive === true);

  const [filters, setFilters] = useState({
    description: '',
    dueDate: null,
    taskStatusId: TASK_STATUS.ACTIVE,
    taskImportanceId: null,
    serviceId: null,
    vendorId: null,
    showCompleted: false,
    showCanceled: false
  });

  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [activeTask, setActiveTask] = useState(null);
  const [activeTaskComments, setActiveTaskComments] = useState([]);
  const [activeModalType, setActiveModalType] = useState(null);
  const [taskModalLoading, setTaskModalLoading] = useState(false);

  const filteredTasks = useMemo(() => {
    const allTasks = Array.isArray(tasks) ? tasks : [];
    let result = [...allTasks];

    if (filters.description) {
      const lowerText = filters.description.toLowerCase();
      result = result.filter(t => (t.description || '').toLowerCase().includes(lowerText));
    }
    if (filters.dueDate) {
      // dueDate is a full timestamp; compare the same yyyy-MM-dd value the Due Date column shows
      result = result.filter(t => formatDateForInput(t.dueDate) === filters.dueDate);
    }
    if (filters.taskStatusId !== ALL_STATUSES) {
      result = result.filter(t => matchesStatusFilter(t, filters.taskStatusId));
    }
    if (filters.taskImportanceId) {
      result = result.filter(t => t.taskImportanceId === filters.taskImportanceId);
    }
    if (filters.serviceId) {
      result = result.filter(t => t.serviceId === filters.serviceId);
    }
    if (filters.vendorId) {
      result = result.filter(t => t.vendorId === filters.vendorId);
    }

    // The toggles add every Completed / Canceled task on top of the filtered list, ignoring the filters above
    const matchingTasks = new Set(result);
    return allTasks.filter(t =>
      matchingTasks.has(t) ||
      (filters.showCompleted && isIdEqual(t.taskStatusId, TASK_STATUS.COMPLETED)) ||
      (filters.showCanceled && isIdEqual(t.taskStatusId, TASK_STATUS.CANCELED)));
  }, [tasks, filters]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value || null }));
  };

  const handleClearFilters = () => {
    setFilters({
      description: '',
      dueDate: null,
      taskStatusId: TASK_STATUS.ACTIVE,
      taskImportanceId: null,
      serviceId: null,
      vendorId: null,
      showCompleted: false,
      showCanceled: false
    });
  };

  // The call modal's View Comments tab lists the authorization comments linked to the task
  const loadTaskComments = async taskId => {
    setActiveTaskComments([]);
    const action = await props.getTaskComments(taskId);
    if (action && action.type && action.type.includes('FAILURE')) {
      props.showToast('Failed to load task comments.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      return;
    }
    setActiveTaskComments(action?.response || []);
  };

  const handleTitleClick = async task => {
    if (isReadOnly) return;

    const taskType = taskTypes.find(t => t.id === task.taskTypeId);
    if (!taskType) return;

    if (taskType.title === 'Call' || taskType.title === 'VendorCall') {
      await loadTaskComments(task.taskId);
      setActiveTask(task);
      setActiveModalType('call');
    } else if (taskType.title === 'Email' || taskType.title === 'VendorEmail') {
      setActiveTask(task);
      setActiveModalType('email');
    } else if (taskType.title === 'Letter' || taskType.title === 'VendorLetter') {
      setActiveTask(task);
      setActiveModalType('email');
    } else if (taskType.title === 'Voucher' || taskType.title === 'VendorVoucher') {
      history.push(`/vouchers?taskId=${task.taskId}`);
    }
  };

  const handleCloseTaskModal = () => {
    setActiveTask(null);
    setActiveModalType(null);
  };

  const handleTaskModalSubmit = async (taskAction, commentText, isClientViewable, taskStatusId) => {
    if (!activeTask) return;
    let newValues = { ...activeTask };
    setTaskModalLoading(true);
    try {
      if(taskAction === TASK_MODAL_ACTION.ATTEMPT) {
        if(newValues.firstAttemptDate == null) { newValues.firstAttemptDate = new Date(); }
        if(taskStatusId) { newValues.taskStatusId = taskStatusId; }
      }
      else if(taskAction === TASK_MODAL_ACTION.COMPLETE) {
        if(newValues.completedDate == null) { newValues.completedDate = new Date(); }
        newValues.taskStatusId = TASK_STATUS.COMPLETED;
      }
      else {
        if(newValues.canceledDate == null) { newValues.canceledDate = new Date(); }
        newValues.taskStatusId = TASK_STATUS.CANCELED;
      }
      const action = await props.updateTask(newValues)
      if (action && action.type && action.type.includes('FAILURE')) {
        props.showToast('Failed to update task. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
        return;
      }

      // The task record has no comment column; notes are saved as an authorization comment linked to the task,
      // the same as on the dashboard
      if (commentText && commentText.trim()) {
        const commentAction = await props.postAuthorizationComment(
          {
            authorizationId: activeTask.fileId,
            taskId: activeTask.taskId,
            serviceId: activeTask.serviceId || null,
            regardingId: activeTask.regardingId || null,
            subject: `${taskAction.subject}: ${activeTask.title}`,
            comment: commentText,
            isTransfereeViewable: false,
            isClientViewable: !!isClientViewable
          },
          activeTask.fileId
        );
        if (commentAction && commentAction.type && commentAction.type.includes('FAILURE')) {
          props.showToast('Task updated but failed to save comment', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
          handleCloseTaskModal();
          return;
        }
      }

      props.showToast(taskAction.successMessage, { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
      handleCloseTaskModal();
    } catch {
      props.showToast('Failed to update task. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } finally {
      setTaskModalLoading(false);
    }
  };

  const handleEmailModalSubmit = async emailData => {
    if (!activeTask) return;
    setTaskModalLoading(true);
    try {
      const action = await props.updateTask({ ...activeTask });
      if (action && action.type && action.type.includes('FAILURE')) {
        props.showToast('Failed to complete task. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      } else {
        props.showToast('Task completed.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
        handleCloseTaskModal();
      }
    } catch {
      props.showToast('Failed to complete task. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } finally {
      setTaskModalLoading(false);
    }
  };

  const handleCreateTask = async payload => {
    setIsCreating(true);
    try {
      const resp = await onCreateTask(payload);
      if (resp && resp.type && resp.type.includes('FAILURE')) {
        props.showToast('Failed to create task. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
      } else {
        props.showToast('Task created successfully.', { severity: TOAST_MESSAGE_SEVERITY_SUCCESS });
        setCreateModalOpen(false);
      }
    } catch {
      props.showToast('Failed to create task. Please try again.', { severity: TOAST_MESSAGE_SEVERITY_ERROR });
    } finally {
      setIsCreating(false);
    }
  };

  const getStatusDisplay = task => {
    const config = STATUS_CONFIG.find(s => isIdEqual(s.id, task.taskStatusId));
    if (!config) return <Typography variant="body2">-</Typography>;
    return (
      <Typography variant="body2" className={classes[config.className]}>
        {config.label}
      </Typography>
    );
  };

  const getImportanceDisplay = task => {
    const config = IMPORTANCE_CONFIG.find(i => isIdEqual(i.id, task.taskImportanceId));
    if (!config) return <Typography variant="body2">-</Typography>;
    return (
      <Typography variant="body2" className={classes[config.className]}>
        {config.label}
      </Typography>
    );
  };

  const isOverdue = task => {
    const statusConf = STATUS_CONFIG.find(s => isIdEqual(s.id, task.taskStatusId));
    return statusConf?.label === 'Overdue';
  };

  const getServiceName = task => allServices.find(s => isIdEqual(s.id, task.serviceId))?.title || '-';

  // The task read procedures return the vendor name joined from Vendor.Record; the vendors list is only a fallback
  const getVendorName = task => {
    if (task.vendorName) return task.vendorName;
    if (!task.vendorId || !vendors) return '-';
    const vendor = vendors.find(v => v.vendorId === task.vendorId);
    return vendor ? vendor.companyName || vendor.vendorName || '-' : '-';
  };

  const selectedVendor = (vendors || []).find(v => v.vendorId === filters.vendorId) || null;

  return (
    <Card className={classes.card}>
      <CardContent>
        <Box className={classes.cardHeader}>
          <Typography variant="h6">Tasks</Typography>
          <Button variant="contained" color="primary" size="small" disabled={isReadOnly} onClick={() => setCreateModalOpen(true)}>
            Create Task
          </Button>
        </Box>

        {/* Filters */}
        <Box className={classes.filtersRow}>
          <TextField
            className={classes.filterControlSmall}
            label="Task"
            value={filters.description || ''}
            onChange={e => handleFilterChange('description', e.target.value)}
            size="small"
            disabled={isReadOnly}
          />

          <TextField
            className={classes.filterControlSmall}
            label="Due Date"
            type="date"
            value={filters.dueDate || ''}
            onChange={e => handleFilterChange('dueDate', e.target.value)}
            InputLabelProps={{ shrink: true }}
            size="small"
            disabled={isReadOnly}
          />

          {/* Left enabled in read-only mode: it defaults to Active, so it is the only way to list every status */}
          <FormControl className={classes.filterControlSmall} size="small">
            <InputLabel>Status</InputLabel>
            <Select value={filters.taskStatusId} onChange={e => handleFilterChange('taskStatusId', e.target.value)}>
              <MenuItem value={ALL_STATUSES}>All</MenuItem>
              {TASK_STATUS_FILTER_OPTIONS.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.filterControlSmall} size="small">
            <InputLabel>Importance</InputLabel>
            <Select value={filters.taskImportanceId || ''} onChange={e => handleFilterChange('taskImportanceId', e.target.value)} disabled={isReadOnly}>
              <MenuItem value="">All</MenuItem>
              {taskImportances.map(i => (
                <MenuItem key={i.id} value={i.id}>
                  {i.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl className={classes.filterControlSmall} size="small">
            <InputLabel>Service</InputLabel>
            <Select value={filters.serviceId || ''} onChange={e => handleFilterChange('serviceId', e.target.value)} disabled={isReadOnly}>
              <MenuItem value="">All</MenuItem>
              {services.map(s => (
                <MenuItem key={s.id} value={s.id}>
                  {s.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Autocomplete
            className={classes.filterControl}
            options={vendors || []}
            getOptionLabel={option => option.companyName || option.vendorName || ''}
            value={selectedVendor}
            onChange={(event, newValue) => handleFilterChange('vendorId', newValue?.vendorId || null)}
            size="small"
            disabled={isReadOnly}
            renderInput={params => <TextField {...params} label="Vendor" />}
          />

          {/* Left enabled in read-only mode so closed tasks can still be viewed */}
          <FormControlLabel
            control={
              <Switch
                checked={!!filters.showCompleted}
                onChange={e => handleFilterChange('showCompleted', e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label="Show All Completed"
          />

          <FormControlLabel
            control={
              <Switch
                checked={!!filters.showCanceled}
                onChange={e => handleFilterChange('showCanceled', e.target.checked)}
                color="primary"
                size="small"
              />
            }
            label="Show All Canceled"
          />

          <Button onClick={handleClearFilters} size="small" disabled={isReadOnly}>
            Clear
          </Button>
        </Box>

        {/* Table */}
        {filteredTasks.length === 0 ? (
          <Typography className={classes.noTasks} color="textSecondary">
            {tasks && tasks.length > 0 ? 'No tasks match the current filters' : 'No tasks for this file'}
          </Typography>
        ) : (
          <TableContainer className={classes.tableContainer}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell className={classes.headerCell}>Task</TableCell>
                  <TableCell className={classes.headerCell}>Due Date</TableCell>
                  <TableCell className={classes.headerCell}>Status</TableCell>
                  <TableCell className={classes.headerCell}>Importance</TableCell>
                  <TableCell className={classes.headerCell}>Service</TableCell>
                  <TableCell className={classes.headerCell}>Vendor</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTasks.map(task => (
                  <TableRow key={task.taskId} className={isOverdue(task) ? classes.overdueRow : ''}>
                    <TableCell>
                      {isReadOnly ? (
                        <Typography variant="body2">{task.description || 'Untitled Task'}</Typography>
                      ) : (
                        <Link component="button" variant="body2" className={classes.clickableTitle} onClick={() => handleTitleClick(task)}>
                          {task.description || 'Untitled Task'}
                        </Link>
                      )}
                    </TableCell>
                    <TableCell><TaskDueDate task={task} /></TableCell>
                    <TableCell>{getStatusDisplay(task)}</TableCell>
                    <TableCell>{getImportanceDisplay(task)}</TableCell>
                    <TableCell>{getServiceName(task)}</TableCell>
                    <TableCell>{getVendorName(task)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </CardContent>

      {createModalOpen && (
        <CreateFileTaskModal
          open={createModalOpen}
          onClose={() => setCreateModalOpen(false)}
          onSubmit={handleCreateTask}
          authorizationDetails={authorizationDetails}
          vendors={vendors}
          enumerations={enumerations}
          isLoading={isCreating}
        />
      )}

      {activeModalType === 'call' && activeTask && (
        <TaskCallModal
          open={true}
          task={activeTask}
          onClose={handleCloseTaskModal}
          onSubmit={handleTaskModalSubmit}
          isLoading={taskModalLoading}
          comments={activeTaskComments}
        />
      )}
      {activeModalType === 'email' && activeTask && (
        <TaskEmailModal
          open={true}
          task={activeTask}
          onClose={handleCloseTaskModal}
          onSubmit={handleEmailModalSubmit}
          isLoading={taskModalLoading}
        />
      )}
    </Card>
  );
};

AuthorizationTasksCard.propTypes = {
  tasks: PropTypes.array,
  vendors: PropTypes.array,
  enumerations: PropTypes.object,
  id: PropTypes.string,
  onCreateTask: PropTypes.func.isRequired,
  authorizationDetails: PropTypes.object,
  showToast: PropTypes.func.isRequired,
  updateTask: PropTypes.func.isRequired,
  postAuthorizationComment: PropTypes.func.isRequired,
  getTaskComments: PropTypes.func,
  isReadOnly: PropTypes.bool,
};

AuthorizationTasksCard.defaultProps = {
  tasks: [],
  vendors: [],
  enumerations: {}
};

export default AuthorizationTasksCard;
